/**
 * Widget styles as a string for injection into Shadow DOM
 * This ensures complete CSS isolation from the host page
 */
export const widgetStyles = `
/* Reset for Shadow DOM */
:host {
  all: initial;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Container */
.easyhelp-widget {
  position: fixed;
  z-index: var(--easyhelp-z-index, 999999);
}

.easyhelp-widget.position-bottom-right {
  bottom: 20px;
  right: 20px;
}

.easyhelp-widget.position-bottom-left {
  bottom: 20px;
  left: 20px;
}

/* Launcher Button */
.easyhelp-launcher {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--easyhelp-primary, #2563eb);
  color: var(--easyhelp-primary-text, #ffffff);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.easyhelp-launcher:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.easyhelp-launcher:active {
  transform: scale(0.95);
}

.easyhelp-launcher svg {
  width: 28px;
  height: 28px;
  fill: currentColor;
}

.easyhelp-launcher.is-open svg.icon-chat {
  display: none;
}

.easyhelp-launcher:not(.is-open) svg.icon-close {
  display: none;
}

/* Chat Panel */
.easyhelp-panel {
  position: absolute;
  bottom: 76px;
  width: 380px;
  max-width: calc(100vw - 40px);
  height: 520px;
  max-height: calc(100vh - 120px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px) scale(0.95);
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
}

.easyhelp-widget.position-bottom-right .easyhelp-panel {
  right: 0;
}

.easyhelp-widget.position-bottom-left .easyhelp-panel {
  left: 0;
}

.easyhelp-panel.is-open {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}

/* Panel Header */
.easyhelp-header {
  padding: 16px 20px;
  background: var(--easyhelp-primary, #2563eb);
  color: var(--easyhelp-primary-text, #ffffff);
  flex-shrink: 0;
}

.easyhelp-header-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
}

.easyhelp-header-subtitle {
  font-size: 13px;
  opacity: 0.9;
}

.easyhelp-header-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
}

.easyhelp-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
}

.easyhelp-status-dot.offline {
  background: #ef4444;
}

/* Messages Area */
.easyhelp-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
}

.easyhelp-messages::-webkit-scrollbar {
  width: 6px;
}

.easyhelp-messages::-webkit-scrollbar-track {
  background: transparent;
}

.easyhelp-messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

/* Message Bubbles */
.easyhelp-message {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.easyhelp-message.customer {
  align-self: flex-end;
  background: var(--easyhelp-primary, #2563eb);
  color: var(--easyhelp-primary-text, #ffffff);
  border-bottom-right-radius: 4px;
}

.easyhelp-message.operator,
.easyhelp-message.system {
  align-self: flex-start;
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.easyhelp-message.sending {
  opacity: 0.7;
}

.easyhelp-message.failed {
  background: #fecaca;
  color: #991b1b;
}

.easyhelp-message-time {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
}

.easyhelp-message-retry {
  font-size: 12px;
  color: #dc2626;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-top: 4px;
}

/* Attachments */
.easyhelp-attachments {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.easyhelp-attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 13px;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
}

.easyhelp-attachment:hover {
  opacity: 0.8;
}

.easyhelp-attachment.customer {
  background: rgba(255, 255, 255, 0.15);
}

.easyhelp-attachment.operator {
  background: rgba(0, 0, 0, 0.05);
}

.easyhelp-attachment-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.easyhelp-attachment-size {
  flex-shrink: 0;
  font-size: 11px;
  opacity: 0.7;
}

.easyhelp-attachment-image {
  display: block;
  margin-top: 4px;
}

.easyhelp-attachment-image img {
  max-width: 100%;
  max-height: 180px;
  border-radius: 8px;
  object-fit: cover;
}

/* Loading States */
.easyhelp-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #6b7280;
}

.easyhelp-loading-dots {
  display: flex;
  gap: 4px;
}

.easyhelp-loading-dots span {
  width: 8px;
  height: 8px;
  background: #9ca3af;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.easyhelp-loading-dots span:nth-child(1) { animation-delay: -0.32s; }
.easyhelp-loading-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* Empty State */
.easyhelp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #6b7280;
  text-align: center;
  padding: 40px 20px;
}

.easyhelp-empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  color: #9ca3af;
}

/* Composer */
.easyhelp-composer {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.easyhelp-composer-form {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.easyhelp-composer-input {
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: border-color 0.2s ease;
}

.easyhelp-composer-input:focus {
  border-color: var(--easyhelp-primary, #2563eb);
}

.easyhelp-composer-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.easyhelp-composer-input::placeholder {
  color: #9ca3af;
}

.easyhelp-composer-send {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--easyhelp-primary, #2563eb);
  color: var(--easyhelp-primary-text, #ffffff);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease, transform 0.1s ease;
  flex-shrink: 0;
}

.easyhelp-composer-send:hover:not(:disabled) {
  opacity: 0.9;
}

.easyhelp-composer-send:active:not(:disabled) {
  transform: scale(0.95);
}

.easyhelp-composer-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.easyhelp-composer-send svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

/* Offline Banner */
.easyhelp-offline-banner {
  padding: 8px 16px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.easyhelp-offline-banner svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* Error Banner */
.easyhelp-error-banner {
  padding: 8px 16px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 12px;
  text-align: center;
}

/* Load More */
.easyhelp-load-more {
  text-align: center;
  padding: 8px;
}

.easyhelp-load-more button {
  font-size: 12px;
  color: #6b7280;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.easyhelp-load-more button:hover {
  background: #f9fafb;
}

.easyhelp-load-more button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile Responsive */
@media (max-width: 480px) {
  .easyhelp-panel {
    width: calc(100vw - 20px);
    height: calc(100vh - 100px);
    bottom: 70px;
  }

  .easyhelp-widget.position-bottom-right .easyhelp-panel,
  .easyhelp-widget.position-bottom-left .easyhelp-panel {
    left: 10px;
    right: 10px;
  }

  .easyhelp-launcher {
    width: 52px;
    height: 52px;
  }
}
`;
